import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, TableData, Toggle } from '@/components';
import {
  showCaughtErrorToast,
  showSuccessToast,
} from '@/components/toast/globalToast';
import SideBarModal from '@/components/Modal/SideBarModal';
import { Column } from '@/types/common';
import { ESCALATION_STATUS_LABEL, EscalationStatus } from '@/types/rental';
import {
  EscalationImpactPreview,
  getRentEscalationSetting,
  getRentEscalationSettingHistory,
  previewRentEscalationSetting,
  RentEscalationSetting,
  RentEscalationSettingHistoryEntry,
  updateRentEscalationSetting,
} from '@/services/api/rentEscalationSetting';
import { formatDateTime } from '@/utils/format';
import {
  FieldError,
  FieldHelper,
  FieldLabel,
  inputClass,
  TextareaField,
} from '@/components';
import { SettingsTabs } from './components/SettingsTabs';
import {
  fromSetting,
  isDirty,
  RentEscalationFormErrors,
  RentEscalationFormValues,
  toUpdateBody,
  validateRentEscalationForm,
} from './rentEscalationForm';

const rungLabel = (value: string): string =>
  ESCALATION_STATUS_LABEL[value as EscalationStatus] ?? value;

/**
 * The impact preview as ops reads it: how many rentals move tomorrow morning,
 * to which rung, and — the number that matters when the toggle is on — how
 * many repossession cases the job would open by itself.
 */
const ImpactPreviewCard: React.FC<{
  preview: EscalationImpactPreview;
  autoEnabled: boolean;
}> = ({ preview, autoEnabled }) => {
  const harsh = preview.escalating.length;
  const tone =
    harsh === 0
      ? 'border-emerald-200 bg-emerald-50'
      : 'border-amber-200 bg-amber-50';
  const stats: { label: string; value: number; emphasis?: boolean }[] = [
    { label: 'Rental diperiksa', value: preview.rentalsChecked },
    { label: 'Pindah rung', value: preview.transitions, emphasis: true },
    { label: '→ Warned', value: preview.toWarned },
    { label: '→ Shutdown Pending', value: preview.toShutdownPending },
    { label: '→ Repo Requested', value: preview.toRepoRequested },
    { label: '→ Normal', value: preview.toNormal },
    { label: 'Di Repo Requested setelah run', value: preview.atRepoRequested },
    {
      label: 'Case repossession dibuat otomatis',
      value: preview.wouldAutoCreateRepossession,
      emphasis: autoEnabled,
    },
  ];
  return (
    <div className={`rounded-xl border p-4 ${tone}`}>
      <div className="flex items-start gap-2 mb-3">
        <i className="ri-information-line text-[18px] text-gray-700 mt-0.5"></i>
        <div>
          <p className="text-[14px] font-medium text-gray-900">
            Dampak pada run eskalasi berikutnya (06.30 WIB)
          </p>
          <p className="text-[13px] text-gray-600">
            Hitungan job dengan nilai di form ini, terhadap rental aktif saat
            ini. Belum ada yang berubah sampai kamu klik Simpan.
          </p>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {stats.map((stat) => (
          <div key={stat.label} className="rounded-lg bg-white/70 px-3 py-2">
            <p className="text-[12px] text-gray-500">{stat.label}</p>
            <p
              className={`text-[18px] font-semibold ${
                stat.emphasis && stat.value > 0
                  ? 'text-[#FA7319]'
                  : 'text-gray-900'
              }`}
            >
              {stat.value}
            </p>
          </div>
        ))}
      </div>
      {harsh > 0 && (
        <div className="mt-3">
          <p className="text-[13px] font-medium text-gray-900 mb-1">
            Naik rung ({harsh}
            {harsh >= 100 ? '+, ditampilkan 100 pertama' : ''})
          </p>
          <div className="max-h-48 overflow-y-auto rounded-lg border border-gray-200 bg-white">
            <table className="min-w-full text-[13px]">
              <tbody>
                {preview.escalating.map((row) => (
                  <tr key={row.rentalID} className="border-b border-gray-100">
                    <td className="px-3 py-1.5 font-mono">{row.rentalCode}</td>
                    <td className="px-3 py-1.5 text-gray-600">
                      {rungLabel(row.from)}
                    </td>
                    <td className="px-1 py-1.5 text-gray-400">→</td>
                    <td className="px-3 py-1.5 font-medium text-gray-900">
                      {rungLabel(row.to)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

const RentEscalationSettingPage: React.FC = () => {
  const [setting, setSetting] = useState<RentEscalationSetting | null>(null);
  const [values, setValues] = useState<RentEscalationFormValues>({
    lateShutdownDays: '',
    lateRepossessionDays: '',
    autoRepossessionEnabled: false,
    notes: '',
  });
  const [errors, setErrors] = useState<RentEscalationFormErrors>({});
  const [history, setHistory] = useState<RentEscalationSettingHistoryEntry[]>(
    []
  );
  const [isLoading, setIsLoading] = useState(true);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [preview, setPreview] = useState<EscalationImpactPreview | null>(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const [current, entries] = await Promise.all([
        getRentEscalationSetting(),
        getRentEscalationSettingHistory(),
      ]);
      setSetting(current);
      setValues(fromSetting(current));
      setHistory(entries);
      setPreview(null);
    } catch (error) {
      showCaughtErrorToast(error, 'Gagal memuat setting');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const update = <K extends keyof RentEscalationFormValues>(
    key: K,
    value: RentEscalationFormValues[K]
  ) => {
    setValues((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
    // A preview describes the numbers it was computed for; a changed field
    // makes it stale, so it goes rather than sit there looking current.
    setPreview(null);
  };

  const validate = (): boolean => {
    const next = validateRentEscalationForm(values);
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  // Null when the API refused or failed; the toast has already said why.
  const fetchPreview = async (): Promise<EscalationImpactPreview | null> => {
    if (!validate()) return null;
    setIsPreviewing(true);
    try {
      const result = await previewRentEscalationSetting(toUpdateBody(values));
      setPreview(result.impactPreview);
      return result.impactPreview;
    } catch (error) {
      showCaughtErrorToast(error, 'Gagal menghitung dampak');
      return null;
    } finally {
      setIsPreviewing(false);
    }
  };

  // Always confirm against a preview of THESE numbers, never a stale one —
  // `update()` clears the preview on every edit, so "have one" means current.
  const openConfirm = async () => {
    const current = preview ?? (await fetchPreview());
    if (current) setIsConfirmOpen(true);
  };

  const save = async () => {
    setIsSaving(true);
    try {
      await updateRentEscalationSetting(toUpdateBody(values));
      showSuccessToast('Berhasil', 'Setting eskalasi disimpan');
      setIsConfirmOpen(false);
      // Same reset path as first load: setting, form, history, no preview.
      await load();
    } catch (error) {
      showCaughtErrorToast(error, 'Gagal menyimpan setting');
    } finally {
      setIsSaving(false);
    }
  };

  const dirty = isDirty(values, setting);

  const historyColumns = useMemo<Column<RentEscalationSettingHistoryEntry>[]>(
    () => [
      {
        key: 'createdAt',
        title: 'Waktu',
        render: (value) => formatDateTime(value),
      },
      { key: 'createdBy', title: 'Oleh' },
      {
        key: 'lateShutdownDays',
        title: 'Shutdown (hari)',
        align: 'right',
      },
      {
        key: 'lateRepossessionDays',
        title: 'Repossession (hari)',
        align: 'right',
      },
      {
        key: 'autoRepossessionEnabled',
        title: 'Auto-create case',
        render: (value: boolean) => (value ? 'Nyala' : 'Mati'),
      },
      {
        key: 'notes',
        title: 'Catatan',
        render: (value) => value || '-',
      },
    ],
    []
  );

  return (
    <div className="bg-white p-6 max-w-full mx-auto">
      <div className="mb-4">
        <h1 className="text-[24px] font-semibold text-gray-900 mb-1">
          Settings
        </h1>
        <p className="text-[14px] text-gray-600">
          Ambang eskalasi tunggakan Dash Rent. Berlaku fleet-wide dan langsung
          ke semua rental aktif pada run eskalasi berikutnya.
        </p>
      </div>
      <SettingsTabs active="rent-escalation" />

      {isLoading ? (
        <div className="py-12 text-center text-[14px] text-gray-500">
          Memuat setting…
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-xl border border-gray-200">
              <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
                <h2 className="text-[16px] font-medium text-gray-900">
                  Ambang tunggakan
                </h2>
                {setting?.updatedAt ? (
                  <span className="text-[12px] text-gray-500">
                    Terakhir diubah {formatDateTime(setting.updatedAt)} oleh{' '}
                    {setting.updatedBy}
                  </span>
                ) : (
                  <span className="text-[12px] text-gray-500">
                    Belum pernah disimpan — memakai default
                  </span>
                )}
              </div>
              <div className="p-5 space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <FieldLabel required>
                      Hari tunggakan sampai shutdown
                    </FieldLabel>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={values.lateShutdownDays}
                      onChange={(e) =>
                        update('lateShutdownDays', e.target.value)
                      }
                      className={inputClass(Boolean(errors.lateShutdownDays))}
                    />
                    <FieldError>{errors.lateShutdownDays}</FieldError>
                    <FieldHelper>
                      Dihitung dari tanggal charge belum bayar tertua. Unit
                      masuk antrean matikan manual dan tim di-mention di Slack.
                    </FieldHelper>
                  </div>
                  <div>
                    <FieldLabel required>
                      Hari tunggakan sampai repossession
                    </FieldLabel>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={values.lateRepossessionDays}
                      onChange={(e) =>
                        update('lateRepossessionDays', e.target.value)
                      }
                      className={inputClass(
                        Boolean(errors.lateRepossessionDays)
                      )}
                    />
                    <FieldError>{errors.lateRepossessionDays}</FieldError>
                    <FieldHelper>
                      Harus lebih besar dari hari shutdown. Rental masuk rung
                      Repo Requested.
                    </FieldHelper>
                  </div>
                </div>

                <div className="rounded-lg border border-gray-200 p-4">
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <p className="text-[14px] text-gray-900">
                        Buat case repossession otomatis
                      </p>
                      <p className="text-[13px] text-gray-500 mt-0.5">
                        Saat nyala, job eskalasi membuka case sendiri begitu
                        rental masuk Repo Requested (severity High, tim ops
                        belum di-assign). Saat mati, tim hanya di-mention untuk
                        membuat case manual.
                      </p>
                    </div>
                    <Toggle
                      checked={values.autoRepossessionEnabled}
                      onChange={(checked) =>
                        update('autoRepossessionEnabled', checked)
                      }
                      ariaLabel="Buat case repossession otomatis"
                    />
                  </div>
                  {values.autoRepossessionEnabled &&
                    !setting?.autoRepossessionEnabled && (
                      <p className="mt-3 text-[13px] text-[#FA7319]">
                        Run pertama setelah dinyalakan akan membuka case untuk
                        semua rental yang sudah berada di Repo Requested. Cek
                        angkanya di pratinjau dampak sebelum menyimpan.
                      </p>
                    )}
                </div>

                <div>
                  <FieldLabel>Catatan perubahan</FieldLabel>
                  <TextareaField
                    value={values.notes}
                    onChange={(value) => update('notes', value)}
                    rows={2}
                    maxLength={500}
                    placeholder="Kenapa diubah? Tercatat di riwayat."
                  />
                </div>

                <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-100">
                  <Button
                    variant="secondary"
                    size="small"
                    onClick={fetchPreview}
                    disabled={isPreviewing || isSaving}
                  >
                    <i className="ri-eye-line text-[12px] mr-1"></i>
                    {isPreviewing ? 'Menghitung…' : 'Pratinjau dampak'}
                  </Button>
                  <Button
                    variant="primary"
                    size="small"
                    onClick={openConfirm}
                    disabled={!dirty || isPreviewing || isSaving}
                  >
                    Simpan
                  </Button>
                </div>
              </div>
            </div>

            {preview && (
              <ImpactPreviewCard
                preview={preview}
                autoEnabled={values.autoRepossessionEnabled}
              />
            )}
          </div>

          <div className="space-y-4">
            <div className="rounded-xl border border-gray-200 p-4 bg-gray-50">
              <p className="text-[14px] font-medium text-gray-900 mb-2">
                Cara kerja
              </p>
              <ul className="text-[13px] text-gray-600 space-y-1.5 list-disc pl-4">
                <li>
                  Hari ke-0 tunggakan: rental jadi Warned, mitra dapat push
                  pengingat.
                </li>
                <li>
                  Tidak ada perintah matikan jarak jauh dari OEM. "Shutdown"
                  berarti unit masuk antrean matikan manual dan tim di-mention.
                </li>
                <li>Siapa yang di-mention diatur di tab Slack Mention.</li>
                <li>
                  Menurunkan angka bisa memindahkan banyak rental sekaligus
                  besok pagi. Lihat pratinjau dampak dulu.
                </li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {!isLoading && (
        <div className="mt-8">
          <h2 className="text-[16px] font-medium text-gray-900 mb-3">
            Riwayat perubahan
          </h2>
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <TableData
              data={history}
              columns={historyColumns}
              size="small"
              emptyState={
                <div className="py-8 text-center text-[13px] text-gray-500">
                  Belum ada perubahan tersimpan.
                </div>
              }
            />
          </div>
        </div>
      )}

      <SideBarModal
        isOpen={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        width="lg"
        header={
          <h2 className="text-xl font-semibold text-gray-900">
            Simpan setting eskalasi?
          </h2>
        }
        footer={
          <div className="flex justify-end gap-2">
            <Button
              variant="secondary"
              size="small"
              onClick={() => setIsConfirmOpen(false)}
              disabled={isSaving}
            >
              Batal
            </Button>
            <Button
              variant="primary"
              size="small"
              onClick={save}
              disabled={isSaving}
            >
              {isSaving ? 'Menyimpan…' : 'Ya, simpan'}
            </Button>
          </div>
        }
      >
        <div className="p-6 space-y-4">
          <p className="text-[14px] text-gray-700">
            Shutdown setelah <b>{values.lateShutdownDays}</b> hari, repossession
            setelah <b>{values.lateRepossessionDays}</b> hari, auto-create case{' '}
            <b>{values.autoRepossessionEnabled ? 'nyala' : 'mati'}</b>. Berlaku
            ke semua rental aktif pada run berikutnya.
          </p>
          {preview && (
            <ImpactPreviewCard
              preview={preview}
              autoEnabled={values.autoRepossessionEnabled}
            />
          )}
        </div>
      </SideBarModal>
    </div>
  );
};

export default RentEscalationSettingPage;
